// ─────────────────────────────────────────────────────────────────────────
// LoginView.tsx
// Ported 1:1 (visually + behaviorally) from login.html.
//
// Preserved exactly:
//   - ADMIN_USERS hardcoded credential list (same plaintext values)
//   - sessionStorage key 'rs_admin_session' and stored shape
//     { loggedIn: true, username, role, loginAt }
//   - ~320ms "checking" delay + ~700ms success-state delay before navigating
//   - Validation messages and order (username required -> password required)
//   - Auto-redirect-if-already-logged-in behavior (now an onLoginSuccess call
//     fired from a useEffect on mount, instead of window.location.href)
//
// Changed (per requirements):
//   - window.location.replace/.href -> onLoginSuccess() callback prop, so the
//     parent (e.g. React Router) decides how to navigate. This component does
//     not import a router directly, so it stays reusable in any routing setup.
//   - Phosphor "web" <i> icon classes -> @phosphor-icons/react components.
//   - Inline <style> block -> Tailwind utility classes, with the few effects
//     that aren't expressible as plain utilities (conic-gradient spin ring,
//     orb float, card-in, shake) defined as small keyframe animations. Tailwind
//     v4 CSS-based config note: add the block at the bottom of this file to
//     your global stylesheet (e.g. src/styles/input.css) inside an
//     `@theme { ... }` or plain `@layer utilities` block so the
//     animate-[...] utilities below resolve.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useRef, useState } from 'react';
import {
  User,
  LockKey,
  Eye,
  EyeSlash,
  WarningCircle,
  SignIn,
  CheckCircle,
  CircleNotch,
  ShieldCheck,
} from '@phosphor-icons/react';
import { authApi, ApiError } from './api';

export interface AdminSession {
  loggedIn: true;
  username: string;
  role: string;
  /** ISO-8601 timestamp */
  loginAt: string;
}

export interface LoginViewProps {
  /**
   * Called once a valid session exists — either because the user just
   * authenticated successfully, or because a valid session was already
   * confirmed with the server on mount (App.tsx handles that check;
   * this component only calls onLoginSuccess after a fresh login here).
   */
  onLoginSuccess: (session: AdminSession) => void;
}

type LoginPhase = 'idle' | 'checking' | 'success';

export default function LoginView({ onLoginSuccess }: LoginViewProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phase, setPhase] = useState<LoginPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  // bump this to retrigger the CSS shake animation even on repeated identical errors
  const [errorTick, setErrorTick] = useState(0);

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const showError = useCallback((msg: string) => {
    setError(msg);
    setErrorTick((t) => t + 1);
  }, []);

  const clearErrorOnType = useCallback(() => {
    if (error) setError(null);
  }, [error]);

  const doLogin = useCallback(() => {
    const u = username.trim();
    const p = password;

    if (!u) {
      showError('กรุณากรอกชื่อผู้ใช้');
      return;
    }
    if (!p) {
      showError('กรุณากรอกรหัสผ่าน');
      return;
    }

    setError(null);
    setPhase('checking');

    // เรียก API จริง (มี delay ธรรมชาติจากเครือข่ายอยู่แล้ว ไม่ต้องจำลอง 320ms อีก)
    authApi
      .login(u, p)
      .then((session) => {
        setPhase('success');
        // คงจังหวะหยุด 700ms ตอนขึ้นสถานะสำเร็จไว้เหมือนเดิม ก่อนพาไปหน้า Dashboard
        window.setTimeout(() => {
          onLoginSuccess({ loggedIn: true, username: session.username, role: session.role, loginAt: session.loginAt });
        }, 700);
      })
      .catch((e: unknown) => {
        setPhase('idle');
        const message =
          e instanceof ApiError ? e.message : 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า XAMPP เปิดใช้งานอยู่';
        showError(message);
        setPassword('');
        passwordInputRef.current?.focus();
      });
  }, [username, password, onLoginSuccess, showError]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') doLogin();
      clearErrorOnType();
    },
    [doLogin, clearErrorOnType],
  );

  const isLoading = phase === 'checking';
  const isSuccess = phase === 'success';
  const isLocked = isLoading || isSuccess;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[linear-gradient(135deg,#150128_0%,#2C0455_35%,#440E7A_65%,#1E0433_100%)] font-[Inter,sans-serif]">
      {/* ── Background orbs ── */}
      <div
        aria-hidden
        className="pointer-events-none fixed -right-[150px] -top-[200px] h-[600px] w-[600px] animate-[float-orb_8s_ease-in-out_infinite] rounded-full bg-[radial-gradient(circle,rgba(66,28,105,0.35)_0%,transparent_70%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -bottom-[150px] -left-[100px] h-[500px] w-[500px] animate-[float-orb_10s_ease-in-out_infinite_reverse] rounded-full bg-[radial-gradient(circle,rgba(171,97,246,0.12)_0%,transparent_70%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle,rgba(171,97,246,0.07)_1px,transparent_1px)] bg-[length:40px_40px]"
      />

      {/* ── Card ── */}
      <div
        className="relative z-10 w-[420px] max-w-[92vw] animate-[card-in_0.65s_cubic-bezier(.21,1.02,.73,1)_both] rounded-[28px] border border-[rgba(171,97,246,0.22)] bg-white/[0.045] px-10 pb-10 pt-12 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_32px_80px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(255,255,255,0.09)] backdrop-blur-2xl max-[480px]:px-[22px] max-[480px]:pb-[30px] max-[480px]:pt-9"
      >
        {/* card accent line */}
        <div className="absolute left-10 right-10 top-0 h-0.5 rounded-b bg-[linear-gradient(90deg,transparent,#AB61F6,transparent)]" />

        {/* ── Logo ── */}
        <div className="mb-7 flex flex-col items-center">
          <div className="relative mb-4 flex h-28 w-28 items-center justify-center">
            {/* pulse glow */}
            <div className="absolute -inset-2.5 -z-10 animate-[logo-pulse_2.5s_ease-in-out_infinite] rounded-full bg-[radial-gradient(circle,rgba(171,97,246,0.25)_0%,transparent_70%)]" />
            {/* spinning conic ring */}
            <div className="absolute -inset-[3px] animate-[spin-ring_5s_linear_infinite] rounded-full bg-[conic-gradient(#AB61F6_0deg,#DEC4F8_60deg,#F9F3FE_90deg,#DEC4F8_120deg,#AB61F6_180deg,#5B2395_240deg,#AB61F6_300deg,#DEC4F8_330deg,#AB61F6_360deg)]" />
            {/* gap ring */}
            <div className="absolute inset-1 rounded-full bg-[rgba(21,1,40,0.85)]" />
            {/* logo circle */}
            <div className="absolute inset-[7px] z-10 flex items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_0_0_1px_rgba(255,255,255,0.15),0_4px_20px_rgba(0,0,0,0.5)]">
              {/*
                Original asset was a base64 JPEG data URI embedded directly in login.html.
                Move it to a real asset (e.g. src/assets/logo.jpg) and import it here:
                  import logoUrl from '@/assets/logo.jpg';
              */}
              <img
                src="/assets/richer-services-logo.jpg"
                alt="Richer Services Logo"
                className="block h-full w-full object-contain p-1.5"
              />
            </div>
          </div>
          <div className="text-center text-[1.4rem] font-extrabold tracking-tight text-white">
            Richer Services
          </div>
          <div className="mt-[3px] text-center text-[0.7rem] uppercase tracking-[0.12em] text-[#DEC4F8]">
            Admin Management System
          </div>
        </div>

        <div className="mb-6 text-center text-[0.92rem] font-medium text-white/55">
          เข้าสู่ระบบผู้ดูแลระบบ
        </div>

        {/* ── Error ── */}
        {error && (
          <div
            key={errorTick}
            className="mb-3.5 flex animate-[shake_0.35s_ease] items-center gap-2 rounded-[10px] border border-[rgba(239,68,68,0.28)] bg-[rgba(239,68,68,0.11)] px-3.5 py-2.5 text-[0.79rem] font-medium text-[#FCA5A5]"
            role="alert"
          >
            <WarningCircle weight="fill" className="shrink-0 text-base" />
            <span>{error}</span>
          </div>
        )}

        {/* ── Username ── */}
        <div className="mb-3.5">
          <label
            htmlFor="inp-user"
            className="mb-1.5 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#DEC4F8]"
          >
            <User weight="fill" className="text-[#AB61F6]" />
            ชื่อผู้ใช้ (Username)
          </label>
          <div className="group relative">
            <User className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[1.1rem] text-white/30 transition-colors group-focus-within:text-[#AB61F6]" />
            <input
              id="inp-user"
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                clearErrorOnType();
              }}
              onKeyDown={handleKeyDown}
              placeholder="กรอกชื่อผู้ใช้"
              autoComplete="username"
              disabled={isLocked}
              className="w-full rounded-[14px] border border-white/[0.11] bg-white/[0.06] py-3.5 pl-11 pr-4 text-[0.92rem] font-medium text-white placeholder:font-normal placeholder:text-white/[0.28] outline-none transition-[border-color,background,box-shadow] focus:border-[#AB61F6] focus:bg-[rgba(171,97,246,0.07)] focus:shadow-[0_0_0_3px_rgba(171,97,246,0.16)] disabled:opacity-60"
            />
          </div>
        </div>

        {/* ── Password ── */}
        <div className="mb-3.5">
          <label
            htmlFor="inp-pass"
            className="mb-1.5 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#DEC4F8]"
          >
            <LockKey weight="fill" className="text-[#AB61F6]" />
            รหัสผ่าน (Password)
          </label>
          <div className="group relative">
            <LockKey className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[1.1rem] text-white/30 transition-colors group-focus-within:text-[#AB61F6]" />
            <input
              ref={passwordInputRef}
              id="inp-pass"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearErrorOnType();
              }}
              onKeyDown={handleKeyDown}
              placeholder="กรอกรหัสผ่าน"
              autoComplete="current-password"
              disabled={isLocked}
              className="w-full rounded-[14px] border border-white/[0.11] bg-white/[0.06] py-3.5 pl-11 pr-11 text-[0.92rem] font-medium text-white placeholder:font-normal placeholder:text-white/[0.28] outline-none transition-[border-color,background,box-shadow] focus:border-[#AB61F6] focus:bg-[rgba(171,97,246,0.07)] focus:shadow-[0_0_0_3px_rgba(171,97,246,0.16)] disabled:opacity-60"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label="แสดง/ซ่อนรหัสผ่าน"
              onClick={() => setShowPassword((v) => !v)}
              disabled={isLocked}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer p-0.5 text-[1.05rem] text-white/30 transition-colors hover:text-[#AB61F6] disabled:cursor-not-allowed"
            >
              {showPassword ? <EyeSlash /> : <Eye />}
            </button>
          </div>
        </div>

        <div className="my-5 h-px bg-[linear-gradient(90deg,transparent,rgba(171,97,246,0.2),transparent)]" />

        {/* ── Submit ── */}
        <button
          type="button"
          onClick={doLogin}
          disabled={isLocked}
          className={[
            'flex w-full items-center justify-center gap-2 rounded-[14px] border-none py-3.5 text-[0.95rem] font-extrabold tracking-[0.02em] text-[#1E0433]',
            'shadow-[0_6px_20px_rgba(171,97,246,0.4),inset_0_1px_0_rgba(255,255,255,0.25)] transition-[transform,box-shadow,filter] duration-150',
            isLocked
              ? 'pointer-events-none opacity-80'
              : 'cursor-pointer hover:-translate-y-px hover:shadow-[0_10px_28px_rgba(171,97,246,0.5)] hover:brightness-[1.06] active:translate-y-px',
            isSuccess
              ? 'bg-[linear-gradient(135deg,#16a34a,#22c55e)]'
              : 'bg-[linear-gradient(135deg,#5B2395_0%,#AB61F6_50%,#AD68F3_100%)]',
          ].join(' ')}
        >
          {isSuccess ? (
            <CheckCircle weight="fill" className="text-[1.1rem]" />
          ) : isLoading ? (
            <CircleNotch className="animate-spin text-[1.1rem]" />
          ) : (
            <SignIn weight="fill" className="text-[1.1rem]" />
          )}
          <span>
            {isSuccess ? 'เข้าสู่ระบบสำเร็จ!' : isLoading ? 'กำลังตรวจสอบ...' : 'เข้าสู่ระบบ'}
          </span>
        </button>

        <div className="mt-[18px] flex items-center justify-center gap-1.5 text-center text-[0.7rem] text-white/[0.26]">
          <ShieldCheck className="text-[#AB61F6] opacity-55" />
          สำหรับเจ้าหน้าที่และผู้ดูแลระบบเท่านั้น
        </div>
      </div>

      <div className="fixed bottom-[18px] left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/[0.07] bg-white/[0.04] px-4 py-1.5 text-[0.66rem] text-white/20">
        © Richer Services — Admin Dashboard v1.0
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add these keyframes to your global stylesheet (e.g. src/styles/input.css),
// below the `@import "tailwindcss";` line, so the animate-[name_...] utility
// classes used above resolve correctly under Tailwind v4's CSS-first config:
//
// @keyframes float-orb {
//   0%, 100% { transform: translateY(0) scale(1); }
//   50%      { transform: translateY(30px) scale(1.05); }
// }
// @keyframes card-in {
//   from { opacity: 0; transform: translateY(32px) scale(0.97); }
//   to   { opacity: 1; transform: translateY(0) scale(1); }
// }
// @keyframes spin-ring {
//   to { transform: rotate(360deg); }
// }
// @keyframes logo-pulse {
//   0%, 100% { opacity: 0.6; transform: scale(1); }
//   50%      { opacity: 1;   transform: scale(1.08); }
// }
// @keyframes shake {
//   0%, 100% { transform: translateX(0); }
//   20%      { transform: translateX(-6px); }
//   40%      { transform: translateX(6px); }
//   60%      { transform: translateX(-4px); }
//   80%      { transform: translateX(4px); }
// }
// ─────────────────────────────────────────────────────────────────────────
